import { FieldWatch, useFormContext } from "@imminently/decisively-ui/Form";
import Typography from "@mui/material/Typography";

import TextField from "@imminently/decisively-ui/TextField";
import SelectField from "@imminently/decisively-ui/SelectField";
import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import MultiSelectField from "@imminently/decisively-ui/MultiSelectField";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import { scrollable } from "@/common";
import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { ATTRIBUTE_TYPES, AttributeInfoFormValues, NONE_ENUM } from "./useAttributeForm";
import { Tooltip } from "@/components";

export const AttributeForm = () => {
  const release = useFullReleaseStrict();
  const canEdit = useIsProjectTechnicalWriter();
  const form = useFormContext<AttributeInfoFormValues>();

  const enumOptions = [{ value: NONE_ENUM, label: "None" }].concat(
    (release?.enums ?? []).map((e) => ({ label: e.id, value: e.id })) ?? [],
  );

  const attributeTypesOptions = ATTRIBUTE_TYPES.map((t) => ({ value: t.value, label: t.key }));

  /* TOOD: needs UI fix:
    - category and phase name should have the same width
    - category and phase name should only appear if their respective checkbox is true
  */
  return (
    <>
      <Typography>System information used to define an attribute.</Typography>
      <FormPanel>
        <FormPanel>
          <TextField
            label="Public name"
            name="publicId"
            disabled={!canEdit}
            fullWidth
          />
          <SelectField
            name="type"
            label="Type"
            options={attributeTypesOptions}
            disabled={!canEdit}
            fullWidth
          />
          <SelectField
            name="enum"
            label="Enumeration"
            options={enumOptions}
            disabled={!canEdit}
            fullWidth
          />
          <MultiSelectField
            name="tags"
            label="Tags"
            options={[]}
            allowArbitrary={true}
            disabled={!canEdit}
            fullWidth
          />
          <Tooltip
            title="Marking this attribute as a category allows for grouping child attributes"
            placement="bottom-start"
          >
            <div className={"flex items-center w-full justify-between gap-3"}>
              <CheckboxField
                name="category"
                label="Category attribute?"
                disabled={!canEdit}
              />
              <TextField
                label="Category name"
                name="categoryName"
                disabled={!canEdit}
                fullWidth
              />
            </div>
          </Tooltip>
          <FieldWatch names={["category"]}>
            {({ category }) =>
              category ? (
                <Tooltip
                  title="Use this attribute as the grouping title when multiple category attributes exist for the same entity"
                  placement="bottom-start"
                >
                  <div className="ml-2">
                    <CheckboxField
                      name="categoryTitle"
                      label="Category Title (used for grouping)"
                      disabled={!canEdit}
                    />
                  </div>
                </Tooltip>
              ) : null
            }
          </FieldWatch>
          <Tooltip
            title="Phase attributes are used in decision flows to provide a grouping of tasks for progress"
            placement="bottom-start"
          >
            <div className={"flex items-center w-full justify-between gap-3"}>
              <CheckboxField
                name="phase"
                label="Phase attribute?"
                disabled={!canEdit}
              />
              <TextField
                label="Phase name"
                name="phaseName"
                disabled={!canEdit}
                fullWidth
              />
            </div>
          </Tooltip>
        </FormPanel>
      </FormPanel>
    </>
  );
};
